import {
  PaymentAttemptOutcome,
  PaymentAttemptStatus,
  PaymentMessage,
  PaymentRetryError,
  PaymentRetryErrorKind,
} from "@types-runway/paymentRetry";

const successText =
  "Payment success! Your order is still processing, we'll notify you when shipped.";
const paymentSafeText = "Your payment is safe. Runway will finish the shipment of your order.";
const genericRefusalText = "Your payment could not be completed.";
const notChargedText = "You were not charged.";
const unconfirmedText =
  "We could not confirm the outcome of your payment. The order below shows its current state.";
const sessionEndedText = "Your session has ended. Please sign in again.";
const retryUnavailableText = "This order cannot be paid here.";
const notStartedText = "We couldn't start your payment.";

const endSentence = (sentence: string): string =>
  /[.!?]$/.test(sentence.trim()) ? sentence.trim() : `${sentence.trim()}.`;

const joinSentences = (...sentences: Array<string | undefined>): string =>
  sentences
    .filter((sentence): sentence is string => Boolean(sentence))
    .map(endSentence)
    .join(" ");

// Only a refusal carries a reason meant for the patient; other server text is internal.
const describeFailure = ({ kind, reason }: PaymentRetryError): PaymentMessage => {
  switch (kind) {
    case PaymentRetryErrorKind.SEND_FAILED:
      return { variant: "success", text: paymentSafeText };
    case PaymentRetryErrorKind.PAYMENT_REFUSED:
      return {
        variant: "error",
        text: joinSentences(reason ?? genericRefusalText, notChargedText),
      };
    case PaymentRetryErrorKind.SESSION_ENDED:
      return { variant: "error", text: sessionEndedText };
    case PaymentRetryErrorKind.RETRY_UNAVAILABLE:
      return { variant: "error", text: retryUnavailableText };
    case PaymentRetryErrorKind.NOT_STARTED:
      return { variant: "error", text: joinSentences(notStartedText, notChargedText) };
    default:
      return { variant: "error", text: unconfirmedText };
  }
};

export const describePaymentOutcome = (outcome: PaymentAttemptOutcome): PaymentMessage =>
  outcome.status === PaymentAttemptStatus.SUCCEEDED
    ? { variant: "success", text: successText }
    : describeFailure(outcome.error);
